import { cn } from '@/lib/cn';

const API_BASE = import.meta.env.VITE_API_URL || '';

// resolveAvatarUrl turns a stored avatar_url into a browser-loadable src.
// Uploaded avatars are served from a relative API path ("/api/v1/avatars/…");
// in dev these are proxied through Vite, in prod they must hit the API origin.
// External URLs (e.g. DiceBear seeds) are returned untouched.
export function resolveAvatarUrl(url?: string | null): string {
  if (!url) return '';
  if (url.startsWith('/')) return API_BASE + url;
  return url;
}

type Rounded = 'full' | '2xl' | 'xl' | 'lg';

const roundedClass: Record<Rounded, string> = {
  full: 'rounded-full',
  '2xl': 'rounded-2xl',
  xl: 'rounded-xl',
  lg: 'rounded-lg',
};

interface AvatarProps {
  /** The stored avatar_url (relative API path or external URL). */
  src?: string | null;
  /** Display name — first letter is the initials fallback. */
  name?: string | null;
  /** Pixel size of the square. */
  size?: number;
  rounded?: Rounded;
  className?: string;
}

/**
 * Avatar renders the user's uploaded image when present, otherwise a gradient
 * initials tile. Single source of truth for every avatar render site
 * (navbar, sidebar, topbar, profile, settings).
 */
export function Avatar({ src, name, size = 40, rounded = 'full', className }: AvatarProps) {
  const resolved = resolveAvatarUrl(src);
  const initial = (name?.trim()?.[0] ?? '?').toUpperCase();
  const dims = { width: size, height: size };

  if (resolved) {
    return (
      <img
        src={resolved}
        alt={name ?? ''}
        style={dims}
        className={cn('flex-shrink-0 object-cover bg-gray-100 border border-black/5', roundedClass[rounded], className)}
      />
    );
  }

  return (
    <div
      style={{
        ...dims,
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        fontSize: Math.max(11, Math.round(size * 0.4)),
      }}
      className={cn('flex flex-shrink-0 items-center justify-center font-bold text-white', roundedClass[rounded], className)}
      aria-label={name ?? undefined}
    >
      {initial}
    </div>
  );
}
